import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Format currency
export function formatCurrency(amount, currency = 'RON') {
    return new Intl.NumberFormat('ro-RO', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
}

// Format date with time (default) - Bucharest timezone
export function formatDate(dateString, options = {}) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Bucharest',
        ...options,
    };
    return new Intl.DateTimeFormat('ro-RO', defaultOptions).format(date);
}

// Format date with time and seconds (exact) - Bucharest timezone
export function formatDateTime(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('ro-RO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Europe/Bucharest',
    }).format(date);
}

// Format date only (no time) - Bucharest timezone
export function formatDateOnly(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('ro-RO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'Europe/Bucharest',
    }).format(date);
}

// Format relative time
export function formatRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'acum câteva secunde';
    if (diffInSeconds < 3600) return `acum ${Math.floor(diffInSeconds / 60)} minute`;
    if (diffInSeconds < 86400) return `acum ${Math.floor(diffInSeconds / 3600)} ore`;
    if (diffInSeconds < 604800) return `acum ${Math.floor(diffInSeconds / 86400)} zile`;
    
    return formatDate(dateString);
}

// Format only time (HH:MM) - Bucharest timezone
export function formatTime(dateString) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ro-RO', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Bucharest',
    }).format(date);
}

// Normalize string - remove diacritics for search
export function normalizeString(str) {
    if (!str) return '';
    // First convert to lowercase
    let result = str.toLowerCase();
    // Replace Romanian diacritics explicitly first
    result = result
        .replace(/[ăâ]/g, 'a')
        .replace(/[îï]/g, 'i')
        .replace(/[șş]/g, 's')
        .replace(/[țţ]/g, 't');
    // Then use NFD normalization for any remaining accents
    result = result.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return result;
}

// Search match - compares normalized strings
export function searchMatch(text, query) {
    if (!text || !query) return false;
    const normalizedText = normalizeString(text);
    const normalizedQuery = normalizeString(query);
    return normalizedText.includes(normalizedQuery);
}

// Get thumbnail URL for an image - adds width and quality params for backend resize
export function getThumbnailUrl(imageUrl, width = 400, quality = 75) {
    if (!imageUrl) return null;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // Handle relative URLs starting with /api
    let fullUrl = imageUrl;
    if (imageUrl.startsWith('/api')) {
        fullUrl = `${backendUrl}${imageUrl}`;
    }
    
    // Only add thumbnail parameters to our API images
    if (fullUrl.includes('/api/images/')) {
        const separator = fullUrl.includes('?') ? '&' : '?';
        return `${fullUrl}${separator}w=${width}&q=${quality}`;
    }
    
    // Return external URLs as-is
    return fullUrl;
}

// Get full image URL (for lightbox/detail views)
export function getFullImageUrl(imageUrl) {
    if (!imageUrl) return null;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // Handle relative URLs starting with /api
    if (imageUrl.startsWith('/api')) {
        return `${backendUrl}${imageUrl}`;
    }
    
    return imageUrl;
}

// Check if device is mobile (client-side only)
export function isMobileDevice() {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
}

// Get optimized thumbnail URL based on device
// Mobile: smaller thumbnails (200px) for bandwidth savings
// Desktop: larger thumbnails (400px) for better quality
export function getResponsiveThumbnailUrl(imageUrl, desktopWidth = 400, mobileWidth = 200, quality = 75) {
    if (!imageUrl) return null;
    
    const width = isMobileDevice() ? mobileWidth : desktopWidth;
    return getThumbnailUrl(imageUrl, width, quality);
}


// Calculate time remaining
export function getTimeRemaining(endDate) {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return { expired: true, text: 'Expirat' };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (days > 0) return { expired: false, text: `${days}z ${hours}h`, days, hours, minutes, seconds };
    if (hours > 0) return { expired: false, text: `${hours}h ${minutes}m`, days, hours, minutes, seconds };
    return { expired: false, text: `${minutes}m ${seconds}s`, days, hours, minutes, seconds, urgent: true };
}

// Get condition label
export function getConditionLabel(condition) {
    const labels = {
        'new': 'Nou',
        'like_new': 'Ca nou',
        'good': 'Bună',
        'fair': 'Acceptabilă',
        'poor': 'Slabă',
    };
    return labels[condition] || condition;
}

// Get status label and color
export function getStatusInfo(status) {
    const info = {
        'active': { label: 'Activ', color: 'success' },
        'sold': { label: 'Vândut', color: 'primary' },
        'expired': { label: 'Expirat', color: 'warning' },
        'cancelled': { label: 'Anulat', color: 'error' },
        'pending_payment': { label: 'Așteaptă plata', color: 'warning' },
        'paid': { label: 'Plătit', color: 'primary' },
        'processing': { label: 'În procesare', color: 'warning' },
        'shipped': { label: 'Expediat', color: 'primary' },
        'delivered': { label: 'Livrat', color: 'success' },
        'completed': { label: 'Finalizat', color: 'success' },
        'disputed': { label: 'În dispută', color: 'error' },
        'pending': { label: 'În așteptare', color: 'warning' },
        'held': { label: 'Blocat', color: 'primary' },
        'released': { label: 'Eliberat', color: 'success' },
        'refunded': { label: 'Rambursat', color: 'warning' },
    };
    return info[status] || { label: status, color: 'muted' };
}

// Get listing type label
export function getListingTypeLabel(type) {
    return type === 'auction' ? 'Licitație' : 'Preț fix';
}

// Truncate text
export function truncateText(text, maxLength = 100) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}

// Generate avatar initials
export function getInitials(name) {
    if (!name) return '?';
    return name
        .split(' ')
        .map(part => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

// Validate email
export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Calculate Bayesian rating display
export function getBayesianRating(rating, count, platformAvg = 3.5, minReviews = 20) {
    return ((rating * count) + (platformAvg * minReviews)) / (count + minReviews);
}

// Debounce function
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Local storage helpers
export const storage = {
    get: (key, defaultValue = null) => {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch {
            return defaultValue;
        }
    },
    set: (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            console.error('LocalStorage error:', e);
        }
    },
    remove: (key) => {
        localStorage.removeItem(key);
    },
};
